/**
 * In-memory auth store (the access token never touches localStorage).
 * The refresh token lives in an HttpOnly cookie scoped to /api/v1/auth.
 */
import { useSyncExternalStore } from "react"

import type { Me } from "@/lib/types"

type AuthState =
  | { status: "loading"; user: null; token: null }
  | { status: "anonymous"; user: null; token: null }
  | { status: "authenticated"; user: Me; token: string }

let state: AuthState = { status: "loading", user: null, token: null }
const listeners = new Set<() => void>()

function emit() {
  for (const l of listeners) l()
}

export const authStore = {
  get: () => state,
  subscribe(listener: () => void) {
    listeners.add(listener)
    return () => listeners.delete(listener)
  },
  signIn(token: string, user: Me) {
    state = { status: "authenticated", user, token }
    emit()
  },
  setToken(token: string, user?: Me) {
    if (state.status === "authenticated") {
      state = { status: "authenticated", user: user ?? state.user, token }
    } else if (user) {
      state = { status: "authenticated", user, token }
    }
    emit()
  },
  /** Replace the signed-in user (e.g. after a verified email change). */
  setUser(user: Me) {
    if (state.status === "authenticated") {
      state = { ...state, user }
      emit()
    }
  },
  signOut() {
    state = { status: "anonymous", user: null, token: null }
    emit()
  },
}

export function useAuth(): AuthState {
  return useSyncExternalStore(authStore.subscribe, authStore.get, authStore.get)
}

export function useMe(): Me {
  const s = useAuth()
  if (s.status !== "authenticated") throw new Error("useMe() outside an authenticated route")
  return s.user
}
